import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './authContext';
import { auth } from '../firebase';
import { signInWithCustomToken, signInWithEmailAndPassword, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { CircularProgress } from '@mui/material';
import { GoogleIcon, AppleIcon, EmailIcon, LockIcon, EyeIcon } from './authIcons';
import { signInWithProvider } from './oauthPopup';

const apiUrl =  process.env.REACT_APP_PROJECT_ID == "shopik-production" ? "https://us-central1-shopik-production.cloudfunctions.net/api_handler/api/" : process.env.REACT_APP_IS_LOCAL == 1 ? "http://localhost:5001/shopik-stage/us-central1/api_handler/api/" : "https://on-request-3lirqocyfq-uc.a.run.app/api/";

const Login = () => {
  const { user, shopikUser, customer, loading } = useAuth();
  const [passwordEmail, setPasswordEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const idToken = urlParams.get("token");
  const redirect = urlParams.get("redirect");
  const oobCode = urlParams.get("oobCode");
  const oobMode = urlParams.get("mode");

  const [resetCodeEmail, setResetCodeEmail] = useState('');
  const [resetCodeChecking, setResetCodeChecking] = useState(true);
  const [resetCodeError, setResetCodeError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    if (oobMode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setResetCodeEmail(email);
          setResetCodeChecking(false);
        })
        .catch((error) => {
          console.error('❌ Invalid or expired reset code:', error);
          setResetCodeError('This password reset link is invalid or has expired. Please request a new one.');
          setResetCodeChecking(false);
        });
    } else {
      setResetCodeChecking(false);
    }
  }, [oobMode, oobCode]);

  useEffect(() => {
    if (redirect && redirect.length > 0 && !loading && customer && Object.keys(customer).length > 0) {
      navigate('/' + redirect)
    }
    else if (!loading && customer && Object.keys(customer).length > 0) {
      navigate('/products-v2/' + user?.uid);
    } else if (!loading && shopikUser && Object.keys(shopikUser).length > 0) {
      navigate('/customer-select');
    }
  }, [loading, user, customer, navigate]);

  useEffect(() => {
    if (idToken) {
      fetch(apiUrl + "verify-auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: idToken }),
    })
    .then((res) => res.json())
    .then((data) => {
      if (data?.customToken) {
        return signInWithCustomToken(auth, data.customToken);
      }
    })
    .catch((error) => console.error("Auth error:", error));
    }
  }, []);

  const signInWithGoogle = () => {
    setPasswordError('');
    setSigningIn(true);
    signInWithProvider(auth, 'google').catch((error) => {
      console.error(error);
      setPasswordError(error.message);
      setSigningIn(false);
    });
  };

  const signInWithApple = () => {
    setPasswordError('');
    setSigningIn(true);
    signInWithProvider(auth, 'apple').catch((error) => {
      console.error(error);
      setPasswordError(error.message);
      setSigningIn(false);
    });
  };

  const friendlyAuthError = (error) => {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/expired-action-code':
      case 'auth/invalid-action-code':
        return 'This password reset link is invalid or has expired. Please request a new one.';
      case 'auth/user-not-found':
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
        return 'Incorrect email or password. If you\'ve only signed in with an email link before, use "Forgot password?" to set one.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const signInWithPassword = async () => {
    setPasswordError('');
    if (!passwordEmail || !password) {
      setPasswordError('Please enter your email and password.');
      return;
    }
    setSigningIn(true);
    try {
      await signInWithEmailAndPassword(auth, passwordEmail, password);
      // Leave signingIn true: the redirect effect fires once authContext finishes
      // resolving the user/customer docs.
    } catch (error) {
      console.error('❌ Error signing in:', error);
      setPasswordError(friendlyAuthError(error));
      setSigningIn(false);
    }
  };

  const resetPassword = async () => {
    setPasswordError('');
    if (!passwordEmail) {
      setPasswordError('Enter your email above first, then click "Forgot password?".');
      return;
    }
    try {
      await fetch(apiUrl + "request-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: passwordEmail }),
      });
      alert(`If an account exists for ${passwordEmail}, a password reset email has been sent. Check your inbox.`);
    } catch (error) {
      console.error('❌ Error sending password reset email:', error);
      setPasswordError('Something went wrong. Please try again.');
    }
  };

  const submitNewPassword = async () => {
    setResetCodeError('');
    if (!newPassword || !confirmNewPassword) {
      setResetCodeError('Please enter and confirm your new password.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setResetCodeError('Passwords do not match.');
      return;
    }
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setResetSuccess(true);
    } catch (error) {
      console.error('❌ Error confirming password reset:', error);
      setResetCodeError(friendlyAuthError(error));
    }
  };

  if (oobMode === 'resetPassword' && oobCode) {
    if (resetCodeChecking) {
      return (
        <div className='login-text'>
          <img src="images/logo.png" loading="lazy" alt="" style={{ maxWidth: '300px' }} />
        </div>
      );
    }

    if (resetSuccess) {
      return (
        <div className="auth-page">
          <div className="auth-card">
            <div className="auth-logo">
              <img src="images/logo.png" loading="lazy" alt="" />
            </div>
            <h1 className="auth-heading">Your password has been reset</h1>
            <button className="auth-submit-button" onClick={() => navigate('/login')}>Sign In</button>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo">
            <img src="images/logo.png" loading="lazy" alt="" />
          </div>
          <h1 className="auth-heading">Set a new password</h1>
          {resetCodeError && !resetCodeEmail && (
            <div className="auth-error" style={{ textAlign: 'center' }}>{resetCodeError}</div>
          )}
          {resetCodeEmail && (
            <>
              <p className="auth-subheading">{resetCodeEmail}</p>
              <div className="auth-field">
                <span className="auth-field-icon"><LockIcon /></span>
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="auth-field">
                <span className="auth-field-icon"><LockIcon /></span>
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
              </div>
              {resetCodeError && <div className="auth-error">{resetCodeError}</div>}
              <button className="auth-submit-button" onClick={submitNewPassword}>Set New Password</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="images/logo.png" loading="lazy" alt="" />
        </div>
        <h1 className="auth-heading">Sign in</h1>
        <p className="auth-subheading">Welcome back to Hemlock</p>

        <button className="auth-oauth-button" onClick={signInWithGoogle} disabled={signingIn}>
          {signingIn ? <CircularProgress size={20} /> : <><GoogleIcon /> Sign in with Google</>}
        </button>
        <button className="auth-oauth-button" onClick={signInWithApple} disabled={signingIn}>
          {signingIn ? <CircularProgress size={20} /> : <><AppleIcon /> Sign in with Apple</>}
        </button>

        <div className="auth-divider">OR</div>

        <div className="auth-field">
          <span className="auth-field-icon"><EmailIcon /></span>
          <input
            type="email"
            placeholder="Email address"
            value={passwordEmail}
            onChange={(e) => setPasswordEmail(e.target.value)}
            disabled={signingIn}
          />
        </div>
        <div className="auth-field">
          <span className="auth-field-icon"><LockIcon /></span>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={signingIn}
          />
          <button type="button" className="auth-field-toggle" onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password visibility" disabled={signingIn}>
            <EyeIcon visible={showPassword} />
          </button>
        </div>

        {passwordError && <div className="auth-error">{passwordError}</div>}

        <button type="button" className="auth-forgot-link" onClick={resetPassword} disabled={signingIn}>
          Forgot password?
        </button>

        <button
          className="auth-submit-button"
          onClick={signInWithPassword}
          disabled={signingIn}
        >
          {signingIn ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Sign In'}
        </button>

        <div className="auth-footer">
          Don't have an account?{' '}
          <Link
            to="/signup"
            className="auth-footer-link"
            style={signingIn ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
            tabIndex={signingIn ? -1 : undefined}
          >
            Create one
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
